import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APIEventSystem } from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { Back, gsap } from "gsap";
import { Graphics, Sprite, Texture } from "pixi.js";

import { AbstractNearWin } from "../../../abstracts/elements/AbstractNearWin";
import { AbstractReel, IReelData } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { ReelPaddingData } from "../../../abstracts/types/interfaces";
import { flat } from "../../../abstracts/utils/ArrayUtils";
import { fadeAndStopSound, playSound, playSoundOnce } from "../../../abstracts/utils/SoundUtils";
import { davidPanningReelStop, normalConfig, symbolConfig } from "../../UCConfig";
import { UCSoundNames, UCSpineNames } from "../../assetsLoader/UCAssetsNames";
import { BlockerStates, ScatterStates, UCSymbolNames, UCSymbolSize } from "../../types/UCEnums";
import { SimpleReelModelEventData } from "../../types/UCInterfaces";
import { fishLandSounds } from "../Sound/UCSoundController";
import { UCReelBlocker } from "./UCReelBlocker";
import { UCBigFishSymbol } from "./symbols/UCBigFishSymbol";
import { UCMysterySymbol } from "./symbols/UCMysterySymbol";
import { UCScatterSymbol } from "./symbols/UCScatterSymbol";
import { UCSymbol, isFish } from "./symbols/UCSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "./symbols/UCSymbolsPool";
import { UCWildSymbol } from "./symbols/UCWildSymbol";

export const reelLandSounds: UCSoundNames[] = [
    UCSoundNames.REEL_LAND_1,
    UCSoundNames.REEL_LAND_2,
    UCSoundNames.REEL_LAND_3,
    UCSoundNames.REEL_LAND_4,
    UCSoundNames.REEL_LAND_5,
    UCSoundNames.REEL_LAND_6,
];

export class UCReel extends AbstractReel {
    private animatedStepData: { y: number };
    private blockersEnabled: boolean = false;
    private masterSpinTimeline: gsap.core.Timeline | undefined;
    private nudgeSteps: number = 0;
    private paddingConfig: ReelPaddingData;
    private reelSet: string[];
    private reelSetIndex: number = 0;
    private isSpinning: boolean = false;
    private stopIndex: number;
    private stopping: boolean = false;
    private stopTimeline: gsap.core.Timeline | undefined;
    private stopTriggerTl: gsap.core.Timeline | undefined;
    private triggerStop: boolean = false;

    public scatterSymbol: UCScatterSymbol = new UCScatterSymbol();
    public hasXBetActive: boolean = false;
    public bottomBlocker: UCReelBlocker;
    public topBlocker: UCReelBlocker;

    protected customStepDuration: number = 0;
    public readonly events: APIEventSystem | undefined;

    constructor(reelId: number, symbols: string[], triggerEvents: boolean = true) {
        super(reelId, symbols);

        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on(`ReelStateChange`, (data: SimpleReelModelEventData) =>
                this.onReelStateChange(data)
            );
        }

        this.customStepDuration = normalConfig.stepDuration!;
    }

    protected override generateInitialSymbols(symbols: string[]): void {
        super.generateInitialSymbols(symbols);

        this.customStepDuration = normalConfig.stepDuration!;
        this.currentSymbols = [];
        this.stopSymbols = [];

        this.reelSet = flat(symbols);
        this.animatedStepData = { y: 0 };

        this.paddingConfig = {
            visualPaddingBefore: 1,
            paddingBefore: 0,
            visibleSymbols: 5,
            paddingAfter: 0,
            visualPaddingAfter: 1,
        };

        const totalInitCount =
            this.paddingConfig.paddingBefore +
            this.paddingConfig.visibleSymbols +
            this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.reelId}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(
                symbols[i],
                i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore
            );
            if ((i === 1 || i === 5) && symbol instanceof UCWildSymbol) {
                symbol.changeToGold();
            }

            this.currentSymbols.push(symbol);
            this.stopSymbols.push(symbol);
        }

        this.updatePosition();
    }

    protected override initializeOtherGraphics() {
        super.initializeOtherGraphics();

        this.scatterNearWin = new AbstractNearWin(
            this,
            normalConfig.nearWinDuration!,
            UCSpineNames.ANTICIPATION,
            `anticipation`
        );
        this.scatterNearWin.name = `scatterNearWin${this.reelId}`;
        this.scatterNearWin.x = 70;
        this.scatterNearWin.y = 280;
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this.reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];
    }

    private getNextSymbol(position: number): AbstractSymbol {
        if (this.stopping && this.stopNormalBetAnimation.length > 0) {
            const symbol = this.stopSymbols[this.stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }

        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position);
    }

    private createNewSymbol(name: string, position: number): AbstractSymbol {
        const symbol = getSymbolFromPool(name);

        this.symbolsContainer.addChild(symbol);
        symbol.reelPosition = position;

        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    private clearSymbols(symbols: AbstractSymbol[]): void {
        let symbol;

        // eslint-disable-next-line no-cond-assign
        while ((symbol = symbols.pop())) {
            returnSymbolToPool(symbol);
        }
    }

    private getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolInReelSet());
        }
        return result;
    }

    public override startNormalBetAnimation(): gsap.core.Timeline {
        this.customStepDuration = normalConfig.stepDuration!;

        const tl = gsap.timeline({
            onStart: () => {
                this.stopSymbols = [];
                this.nudgeSteps = 0;
            },
            onComplete: () => {
                this.startSpin();

                this.bottomBlocker.showSeparator();
                this.topBlocker.showSeparator();
            },
        });

        const tween = this.step();
        tween.vars.ease = Back.easeIn.config(3);
        tween.totalDuration(normalConfig.stepDuration! * 8);

        tl.add(tween);

        return tl;
    }

    public override stopNormalBetAnimation(
        data: IReelData,
        onReelComplete: Function,
        onLandStart: Function = () => {}
    ): gsap.core.Timeline {
        this.clearSymbols(this.stopSymbols);

        this.customStepDuration = normalConfig.stepDuration!;
        this.stopSymbols = [];
        data.stopSymbolNames = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(
            data.stopSymbolNames!
        );
        data.stopSymbolNames = data.stopSymbolNames.concat(
            this.getRandomSymbols(this.paddingConfig.visualPaddingAfter)
        );

        for (let i: number = 0; i < data.stopSymbolNames.length; i++) {
            const symbol = this.createNewSymbol(
                data.stopSymbolNames[i],
                i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore
            );
            symbol.y =
                normalConfig.stepSize! *
                -(
                    this.paddingConfig.visualPaddingBefore +
                    this.paddingConfig.paddingBefore +
                    this.paddingConfig.visibleSymbols
                );

            if (
                (this.gameMode !== AbstractGameMode.NORMAL || i === 1 || i === 5) &&
                symbol instanceof UCWildSymbol
            ) {
                symbol.changeToGold();
            }

            if (symbol instanceof UCScatterSymbol && i > 1 && i < 5) {
                this.scatterSymbol = symbol;
            }

            this.stopSymbols.push(symbol);
        }

        this.stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                this.stopIndex = data.stopSymbolNames!.length - 1;
                this.stopping = true;

                this.bottomBlocker.symbol = this.stopSymbols[5] as UCSymbol;
            },
            onComplete: () => {
                this.stopTimeline = undefined;
                this.stopping = false;
                this.assignSymbolsToBlockers();
            },
        });

        const numberOfNormalSteps =
            this.paddingConfig.visualPaddingBefore +
            this.paddingConfig.paddingBefore +
            this.paddingConfig.visibleSymbols +
            this.paddingConfig.paddingAfter +
            this.paddingConfig.visualPaddingAfter;

        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step();
            this.stopTimeline.add(tween);
        }

        this.stopTimeline.add(() => {
            this.bounce(0.4, data.hasNearWin ? 2 : 3, onReelComplete, onLandStart);
        });

        this.stopTriggerTl = gsap.timeline({
            onStart: () => {
                this.isSpinning = false;
                this.triggerStop = true;
            },
            onComplete: () => {},
        });

        this.stopTriggerTl.add(
            gsap.to(this, {
                duration: this.stopTimeline.totalDuration(),
            })
        );

        const teaseBlockedSymbolsLabel: string = `teaseBlockedSymbols`;

        this.stopTriggerTl.addLabel(teaseBlockedSymbolsLabel);

        return this.stopTriggerTl;
    }

    protected bounce(
        bounceFactor: number = 0.2,
        durationFactor: number = 1,
        onReelComplete: Function = () => {},
        onLandStart: Function = () => {}
    ): gsap.core.Timeline {
        const tl = gsap.timeline();
        const overshoot = gsap.to(this.animatedStepData, {
            duration: normalConfig.stepDuration! * durationFactor,
            y: normalConfig.stepSize! * bounceFactor,
            onUpdate: () => this.updatePosition(),
            onStart: () => {
                if (
                    this._gameMode !== AbstractGameMode.NORMAL &&
                    this.topBlocker.state === BlockerStates.OPEN &&
                    !this.topBlocker.isAlreadyRevealed
                ) {
                    this.topBlocker.symbol.visible = false;
                }

                onLandStart(this.reelId);

                playSound({
                    name: reelLandSounds[this.reelId],
                    stereo: davidPanningReelStop[this.reelId],
                });
            },
        });

        const back = gsap.to(this.animatedStepData, {
            duration: normalConfig.stepDuration! * durationFactor * 2.5,
            y: 0,
            ease: `back.out(2)`,
            onStart: () => {
                this.handleSoundsOnLand();

                if (this.reelId === 4) {
                    gsap.delayedCall(0.15, () => {
                        fadeAndStopSound({
                            name: UCSoundNames.REELS_SPIN,
                            from: 1,
                            to: 0,
                            duration: 0.8,
                        });

                        fadeAndStopSound({
                            name: UCSoundNames.SCATTER_ANTICIPATION,
                            duration: 0.5,
                            from: 1,
                            to: 0,
                        });
                    });
                }
            },
            onUpdate: () => this.updatePosition(),
            onComplete: () => {
                onReelComplete(this.reelId);
                this.topBlocker.hideFSSymbol();
                this.bottomBlocker.hideFSSymbol();
            },
        });

        tl.add(overshoot);
        tl.add(back);

        const landingTimeline = gsap.timeline({});

        this.currentSymbols.forEach((symbol, symbolIndex) => {
            if (symbolIndex >= 1 && symbolIndex <= 5) {
                if (isFish(symbol.name as UCSymbolNames) || symbol.name === UCSymbolNames.SCATTER) {
                    landingTimeline.add(symbol.getLandingAnimation(), 0);
                }
            }
        });
        landingTimeline.totalDuration(tl.totalDuration() * 0.9);

        tl.add(landingTimeline, 0);

        return tl;
    }

    private step(): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration: this.customStepDuration,
            y: normalConfig.stepSize!,
            ease: `none`,
            onStart: () => this.addSymbol(),
            onUpdate: () => {
                this.updatePosition();
            },
            onComplete: () => this.onAnimationComplete(),
        });

        return tween;
    }

    private spin(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this.isSpinning) {
                    this.masterSpinTimeline?.add(this.spin());
                }
            },
        });
        tl.add(this.step());
        return tl;
    }

    private startSpin() {
        this.isSpinning = true;
        this.masterSpinTimeline = gsap.timeline({});
        this.masterSpinTimeline.add(this.spin());

        this.currentSymbols.forEach((symbol) => {
            (symbol as UCSymbol).isSpinning = true;
        });
    }

    private onAnimationComplete() {
        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.reelPosition += 1;
            symbol.positionInArray = i;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this.stopTimeline?.resume();
        }

        this.updateIndex();
        this.removeSymbolFromReel();
        this.updatePosition();
    }

    private assignSymbolsToBlockers() {
        this.topBlocker.symbol = this.stopSymbols[1] as UCSymbol;
        this.bottomBlocker.symbol = this.stopSymbols[5] as UCSymbol;
    }

    private updateIndex() {
        const valueChange = -1;
        if (this.stopping) {
            this.setStopIndex(this.stopIndex + valueChange);
        } else {
            this.setReelIndex(this.reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this.stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this.stopSymbols.length - 1;
        }
        this.stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this.reelSetIndex = value;
    }

    private addSymbol() {
        let newSymbols = [];

        const newSymbolPosition = this.currentSymbols[0].reelPosition - 1;
        const newSymbol = this.getNextSymbol(newSymbolPosition);
        if (this.gameMode !== AbstractGameMode.NORMAL && newSymbol instanceof UCWildSymbol) {
            newSymbol.changeToGold();
        }

        newSymbols.push(newSymbol);

        this.currentSymbols = this.currentSymbols.concat(newSymbols);
        this.currentSymbols.sort((a: any, b: any) => {
            return a.reelPosition < b.reelPosition ? -1 : 1;
        });

        if (this.isSpinning) {
            this.currentSymbols.forEach((symbol) => {
                (symbol as UCSymbol).isSpinning = true;
            });
        }
    }

    private removeSymbolFromReel() {
        for (let i: number = this.currentSymbols.length - 1; i >= 0; i--) {
            const symbol = this.currentSymbols[i];
            if (
                symbol.reelPosition >=
                this.paddingConfig.visibleSymbols +
                    this.paddingConfig.paddingAfter +
                    this.paddingConfig.visualPaddingAfter
            ) {
                //Remove from bottom
                const removedSymbol = this.currentSymbols.splice(i, 1)[0];
                returnSymbolToPool(removedSymbol);
            }
            if (
                symbol.reelPosition <
                -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore
            ) {
                //Remove from top
                const removedSymbol = this.currentSymbols.splice(i, 1)[0];
                returnSymbolToPool(removedSymbol);
            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            const basePos = normalConfig.stepSize! * symbol.reelPosition;

            symbol.y = basePos + this.animatedStepData.y;
        }
    }

    public override getWinningSymbols(winningArr: boolean[]): UCSymbol[] {
        const visibleSymbols = this.getAllVisibleStopSymbols();

        const winningSymbols: UCSymbol[] = [];

        visibleSymbols.forEach((symbol, index) => {
            if (winningArr[index]) {
                winningSymbols.push(symbol as UCSymbol);
            }
        });

        return winningSymbols;
    }

    public getNonWinningSymbols(winningArr: boolean[]): UCSymbol[] {
        const visibleSymbols = this.getAllVisibleStopSymbols();

        const nonWinningSymbols: UCSymbol[] = [];

        visibleSymbols.forEach((symbol, index) => {
            if (!winningArr[index]) {
                nonWinningSymbols.push(symbol as UCSymbol);
            }
        });

        return nonWinningSymbols;
    }

    public getAllVisibleStopSymbols(): AbstractSymbol[] {
        const paddingBefore =
            this.paddingConfig.visualPaddingBefore +
            this.paddingConfig.paddingBefore +
            this.nudgeSteps;

        return this.stopSymbols.slice(
            paddingBefore,
            paddingBefore + this.paddingConfig.visibleSymbols
        );
    }

    public override getShowNearWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const showNearWinAnimation = super.getShowNearWinAnimation();
        const speedObj: { value: number } = { value: normalConfig.stepDuration! };

        const speedUpSpin = gsap.to(speedObj, {
            value: normalConfig.stepDuration! * 0.7,
            duration: normalConfig.nearWinDuration! / 2,
            onUpdate: () => {
                this.customStepDuration = speedObj.value;
            },
        });

        const tl = gsap.timeline();
        tl.add(showNearWinAnimation, 0);
        tl.add(speedUpSpin, 0);

        return tl;
    }

    public enableFishCatcher(): gsap.core.Timeline {
        const tl = gsap.timeline();
        // tl.add(this.bottomBlocker.getLockAnimation(), 0);
        // tl.add(this.topBlocker.getLockAnimation(), 0);

        this.blockersEnabled = false;

        return tl;
    }

    public clearCurrentSymbols(): void {
        this.currentSymbols.forEach((symbol) => {
            returnSymbolToPool(symbol);
        });

        super.clearCurrentSymbols();
    }

    public getMysteryAnimation(
        symbolsSizes: number[],
        symbolsToReplace: string[]
    ): gsap.core.Timeline {
        //this method handles the xways symbol split
        const tl = gsap.timeline({
            autoRemoveChildren: true,
            smoothChildTiming: true,
        });
        const topBlockerIsMystery = this.topBlocker.symbol.name === UCSymbolNames.MYSTERY;
        const bottomBlockerIsMystery = this.bottomBlocker.symbol.name === UCSymbolNames.MYSTERY;
        let newYPosition = this.topBlocker.symbol.y;
        let newReelPosition = this.topBlocker.symbol.reelPosition;

        const handleSymbolAnimation = (
            symbol: UCSymbol,
            size: number,
            symbolToReplace?: UCSymbolNames
        ) => {
            if (symbol.y !== newYPosition) {
                const moveSymbolAnimation = gsap.to(symbol, {
                    duration: 0.18,
                    y: newYPosition,
                });

                tl.add(moveSymbolAnimation, 0);
            }

            symbol.size = size;
            symbol.reelPosition = newReelPosition;

            newYPosition += symbolConfig.symbolHeight * symbol.size;
            newReelPosition += symbol.size;

            const resizeAnimation = symbol.getResizeAnimation(size);

            tl.add(resizeAnimation, 0);

            if (symbolToReplace) {
                tl.add((symbol as UCMysterySymbol).replaceSymbolAnimation(symbolToReplace), 1.1);
            }
        };

        if (topBlockerIsMystery && bottomBlockerIsMystery) {
            (this.topBlocker.symbol as UCMysterySymbol).multiplier = symbolsSizes[0];
            handleSymbolAnimation(
                this.topBlocker.symbol,
                UCSymbolSize.X2,
                symbolsToReplace[0] as UCSymbolNames
            );

            for (let i = 2; i < 5; i++) {
                handleSymbolAnimation(this.currentSymbols[i] as UCSymbol, UCSymbolSize.X033);
            }

            (this.bottomBlocker.symbol as UCMysterySymbol).multiplier = symbolsSizes[4];
            handleSymbolAnimation(
                this.bottomBlocker.symbol,
                UCSymbolSize.X2,
                symbolsToReplace[4] as UCSymbolNames
            );

            this.bottomBlocker.hideSeparator();
            this.topBlocker.hideSeparator();
        } else if (topBlockerIsMystery) {
            (this.topBlocker.symbol as UCMysterySymbol).multiplier = symbolsSizes[0];
            handleSymbolAnimation(
                this.topBlocker.symbol,
                UCSymbolSize.X2,
                symbolsToReplace[0] as UCSymbolNames
            );

            for (let i = 2; i <= 5; i++) {
                handleSymbolAnimation(this.currentSymbols[i] as UCSymbol, UCSymbolSize.X075);
            }
            this.bottomBlocker.hideSeparator();
            this.topBlocker.hideSeparator();
        } else if (bottomBlockerIsMystery) {
            for (let i = 1; i < 5; i++) {
                handleSymbolAnimation(this.currentSymbols[i] as UCSymbol, UCSymbolSize.X075);
            }

            (this.bottomBlocker.symbol as UCMysterySymbol).multiplier = symbolsSizes[4];
            handleSymbolAnimation(
                this.bottomBlocker.symbol,
                UCSymbolSize.X2,
                symbolsToReplace[4] as UCSymbolNames
            );
            this.bottomBlocker.hideSeparator();
            this.topBlocker.hideSeparator();
        }

        return tl;
    }

    public revealFsSymbol(
        symbolName: UCSymbolNames,
        isTop: boolean,
        isSfs: boolean
    ): gsap.core.Timeline {
        const blocker = isTop ? this.topBlocker : this.bottomBlocker;

        let bgTextureName: string = ``;
        if (isSfs) {
            bgTextureName = blocker.isTop
                ? `sfs_top_blocker_${this.reelId}`
                : `sfs_bottom_blocker_${this.reelId}`;
        } else {
            bgTextureName = blocker.isTop
                ? `fs_top_blocker_${this.reelId}`
                : `fs_bottom_blocker_${this.reelId}`;
        }

        blocker.fakeBackground = new Sprite(Texture.from(bgTextureName));
        blocker.fakeBackground.name = `revealBg`;
        blocker.fakeBackground.x = this.reelId === 0 ? 1 : 0;
        blocker.fakeBackground.y = isTop ? 4 : 448;

        const symbolCloneMask = new Graphics();
        symbolCloneMask.beginFill(0xffffff);
        symbolCloneMask.drawRect(0, 0, 134, 100);
        symbolCloneMask.endFill();
        symbolCloneMask.x = blocker.fakeBackground.x;
        symbolCloneMask.y = blocker.fakeBackground.y;
        blocker.symbolCloneMask = symbolCloneMask;

        blocker.symbolClone = getSymbolFromPool(symbolName) as UCSymbol;
        if (blocker.symbolClone instanceof UCWildSymbol) {
            blocker.symbolClone.changeToGold();
        }
        blocker.symbolClone.x = 0;
        blocker.symbolClone.y = isTop ? -50 : 410;
        blocker.symbolClone.alpha = 0;

        blocker.hasClone = true;

        blocker.symbolClone.mask = symbolCloneMask;

        const tl = gsap.timeline({
            onStart: () => {
                this.addChild(blocker.fakeBackground);
                this.addChild(blocker.symbolClone);
                this.addChild(symbolCloneMask);
            },
        });

        const showSymbolTween = gsap.to(blocker.symbolClone, {
            alpha: 1,
            duration: 0.8,
            ease: `power1.in`,
        });
        tl.add(showSymbolTween, 0);

        const moveSymbolTween = gsap.to(blocker.symbolClone, {
            y: blocker.isTop ? 0 : 444,
            duration: 1,
            ease: `back.out(7)`,
        });
        tl.add(moveSymbolTween, 0.2);

        tl.timeScale(2);

        return tl;
    }

    public override getScatterAnimation(
        scatterState: ScatterStates = ScatterStates.NORMAL
    ): gsap.core.Tween | gsap.core.Timeline {
        return this.scatterSymbol.getWinAnimation(scatterState);
    }

    public stopScatterAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return this.scatterSymbol.getStopWinAnimation();
    }

    public override handleSoundsOnLand() {
        super.handleSoundsOnLand();

        this.currentSymbols.forEach((symbol, symbolIndex) => {
            if (symbol instanceof UCWildSymbol) {
                if (!symbol.isGold && symbolIndex > 1 && symbolIndex < 5) {
                    playSoundOnce({
                        name: UCSoundNames.SILVER_WILD_LAND,
                        stereo: davidPanningReelStop[this.reelId],
                        playOnceMinDuration: 2,
                    });
                }
            } else if (symbol instanceof UCBigFishSymbol) {
                if (symbolIndex > 1 && symbolIndex < 5) {
                    playSoundOnce({
                        name: UCSoundNames.BIG_FISH_LAND,
                        stereo: davidPanningReelStop[this.reelId],
                        playOnceMinDuration: 2,
                    });
                }
            } else if (isFish(symbol.name as UCSymbolNames)) {
                const fishNumber = +symbol.name.replace(/^\D+/g, ``);

                if (symbolIndex > 1 && symbolIndex < 5) {
                    playSoundOnce({
                        name: fishLandSounds[fishNumber - 1],
                        stereo: davidPanningReelStop[this.reelId],
                        playOnceMinDuration: 2,
                    });
                }
            }
        });
    }

    public override resetOnStartSpin() {
        super.resetOnStartSpin();

        this.currentSymbols.forEach((symbol) => {
            symbol.resetOnStartSpin();
        });
    }
}

export default UCReel;
